import { useCallback, useEffect, useState } from 'react';

/**
 * Installing Varza on a desktop or a phone.
 *
 * Chromium browsers offer the installation through an event; Safari never does, and expects the
 * reader to use the share sheet. Both are real ways to install the same application, so this
 * module reports which one applies rather than hiding the feature where the event is missing.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** How the operator can install on the browser they are using. */
export type InstallRoute =
  /** The browser offered an installation prompt, so a button can open it. */
  | 'prompt'
  /** iOS and iPadOS Safari: Share, then "Add to Home Screen". */
  | 'ios-share'
  /** Desktop Safari: File, then "Add to Dock". */
  | 'safari-dock'
  /** Nothing to offer, or already installed. */
  | 'none';

type Listener = () => void;

const listeners = new Set<Listener>();
let deferredPrompt: BeforeInstallPromptEvent | null = null;

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * Captured at import time. The browser fires this once, early, and a listener registered inside a
 * component effect routinely misses it — which is why so many installable sites have a button that
 * never appears.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    // Held rather than shown: Varza offers the installation from its own surfaces, at a moment
    // the reader has chosen, instead of over whatever they were reading.
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

/*
 * How the invitation is declined.
 *
 * Two answers, because they are two different answers. "Not now" is a busy person clearing
 * something off the screen, and it lasts as long as the browser session — a reload does not bring
 * it back, and tomorrow it may be welcome. "Don't show this again" is a decision, and it is kept
 * on the device until the operator reverses it from Settings, where installing is always available
 * whatever they chose here.
 */

const SUPPRESSED_KEY = 'varza.install.reminder-off';
const SESSION_KEY = 'varza.install.dismissed-session';

function readFlag(store: Storage | undefined, key: string): boolean {
  try {
    return store?.getItem(key) === '1';
  } catch {
    return false;
  }
}

function writeFlag(store: Storage | undefined, key: string, value: boolean): void {
  try {
    if (value) store?.setItem(key, '1');
    else store?.removeItem(key);
  } catch {
    // A browser with storage blocked simply asks once per load. There is nothing else to do,
    // and it must not stop the application from rendering.
  }
}

/** True while the operator has asked never to be reminded on this device. */
export function installReminderSuppressed(): boolean {
  return readFlag(globalThis.localStorage, SUPPRESSED_KEY);
}

/** True while the invitation has been cleared for this browser session only. */
export function installDismissedForSession(): boolean {
  return readFlag(globalThis.sessionStorage, SESSION_KEY);
}

/** True when the current window is the installed application rather than a browser tab. */
export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false;
  const standalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  return standalone
    || (typeof window.matchMedia === 'function'
      && ['standalone', 'minimal-ui', 'window-controls-overlay'].some((mode) => window.matchMedia(`(display-mode: ${mode})`).matches));
}

function detectRoute(): InstallRoute {
  if (typeof window === 'undefined' || isInstalled()) return 'none';
  if (deferredPrompt !== null) return 'prompt';

  const agent = window.navigator.userAgent;
  const isAppleTouch = /iPad|iPhone|iPod/u.test(agent)
    // iPadOS reports itself as a Mac; a touch-capable "Mac" is an iPad.
    || (/Macintosh/u.test(agent) && window.navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|android|crios|fxios|edgios).)*safari/iu.test(agent);

  if (isAppleTouch && isSafari) return 'ios-share';
  if (isSafari) return 'safari-dock';
  return 'none';
}

export interface InstallState {
  readonly route: InstallRoute;
  readonly installed: boolean;
  /** True while the operator has asked never to be reminded on this device. */
  readonly reminderSuppressed: boolean;
  /** True while the invitation has been cleared for this browser session. */
  readonly dismissedForSession: boolean;
  /** Opens the browser's installation prompt. Resolves to whether it was accepted. */
  install(): Promise<boolean>;
  /** "Not now": clears the invitation until the next browser session. */
  dismissForSession(): void;
  /** "Don't show this again": kept on the device until Settings reverses it. */
  suppressReminder(): void;
  /** Brings the invitation back, from Settings. */
  restoreReminder(): void;
}

export function useInstall(): InstallState {
  const [route, setRoute] = useState<InstallRoute>(() => detectRoute());
  const [installed, setInstalled] = useState(() => isInstalled());
  const [reminderSuppressed, setReminderSuppressed] = useState(() => installReminderSuppressed());
  const [dismissedForSession, setDismissedForSession] = useState(() => installDismissedForSession());

  useEffect(() => {
    const update = () => {
      setRoute(detectRoute());
      setInstalled(isInstalled());
      // Read back rather than trusted from the setter, so the banner and the Settings panel — two
      // components over one preference — never disagree about it.
      setReminderSuppressed(installReminderSuppressed());
      setDismissedForSession(installDismissedForSession());
    };
    listeners.add(update);
    update();

    // Installing from the browser's own menu changes the display mode without firing
    // `appinstalled` in every browser, so the mode itself is watched too.
    const media = typeof window.matchMedia === 'function' ? window.matchMedia('(display-mode: standalone)') : null;
    media?.addEventListener('change', update);

    return () => {
      listeners.delete(update);
      media?.removeEventListener('change', update);
    };
  }, []);

  const install = useCallback(async () => {
    const event = deferredPrompt;
    if (event === null) return false;
    await event.prompt();
    const { outcome } = await event.userChoice;
    // The event is single-use whatever the answer.
    deferredPrompt = null;
    notify();
    return outcome === 'accepted';
  }, []);

  const dismissForSession = useCallback(() => {
    writeFlag(globalThis.sessionStorage, SESSION_KEY, true);
    notify();
  }, []);

  const suppressReminder = useCallback(() => {
    writeFlag(globalThis.localStorage, SUPPRESSED_KEY, true);
    notify();
  }, []);

  const restoreReminder = useCallback(() => {
    writeFlag(globalThis.localStorage, SUPPRESSED_KEY, false);
    // Reversing the decision from Settings means it is wanted now, not after a restart.
    writeFlag(globalThis.sessionStorage, SESSION_KEY, false);
    notify();
  }, []);

  return {
    route,
    installed,
    reminderSuppressed,
    dismissedForSession,
    install,
    dismissForSession,
    suppressReminder,
    restoreReminder,
  };
}
